
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface StrategySummary {
  name: string;
  exchange: string;
  aum: number;
  num_follower: number;
  pnl_ratio: number;
  timestamp: string;
}

interface ExchangeMetrics {
  totalAum: number;
  totalFollowers: number;
  averagePnl: number;
  strategyCount: number;
}

interface GlobalMetrics {
  totalAum: number;
  totalFollowers: number;
  averagePnl: number;
  strategyCount: number;
  exchangeCount: number;
}

export const useStrategySummary = () => {
  const [data, setData] = useState<StrategySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [globalMetrics, setGlobalMetrics] = useState<GlobalMetrics>({
    totalAum: 0,
    totalFollowers: 0,
    averagePnl: 0,
    strategyCount: 0,
    exchangeCount: 0
  });
  const [exchangeMetrics, setExchangeMetrics] = useState<Record<string, ExchangeMetrics>>({});
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('strategy_summary')
          .select('*')
          .order('exchange', { ascending: true });
          
        if (error) throw new Error(error.message);
        
        if (data) {
          // Process data
          setData(data);
          processMetrics(data);
        }
      } catch (err: any) {
        console.error("Error fetching strategy summary:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const processMetrics = (data: StrategySummary[]) => {
    // Process exchange-level metrics
    const exchanges: Record<string, ExchangeMetrics> = {};
    let globalAum = 0;
    let globalFollowers = 0;
    let globalPnlSum = 0;
    
    data.forEach(strategy => {
      const exchange = strategy.exchange;
      
      // Initialize exchange metrics if not exists
      if (!exchanges[exchange]) {
        exchanges[exchange] = {
          totalAum: 0,
          totalFollowers: 0,
          averagePnl: 0,
          strategyCount: 0
        };
      }
      
      // Update exchange metrics
      exchanges[exchange].totalAum += Number(strategy.aum) || 0;
      exchanges[exchange].totalFollowers += Number(strategy.num_follower) || 0;
      exchanges[exchange].strategyCount++;
      
      // Update global sums
      globalAum += Number(strategy.aum) || 0;
      globalFollowers += Number(strategy.num_follower) || 0;
      globalPnlSum += Number(strategy.pnl_ratio) || 0;
    });
    
    // Calculate average PnL for each exchange
    Object.keys(exchanges).forEach(exchange => {
      const exchangeStrategies = data.filter(s => s.exchange === exchange);
      const pnlSum = exchangeStrategies.reduce((sum, strategy) => sum + (Number(strategy.pnl_ratio) || 0), 0);
      exchanges[exchange].averagePnl = exchangeStrategies.length > 0 
        ? pnlSum / exchangeStrategies.length 
        : 0;
    });
    
    // Set global metrics
    setGlobalMetrics({
      totalAum: globalAum,
      totalFollowers: globalFollowers,
      averagePnl: data.length > 0 ? globalPnlSum / data.length : 0,
      strategyCount: data.length,
      exchangeCount: Object.keys(exchanges).length
    });
    
    setExchangeMetrics(exchanges);
  };
  
  return { 
    data, 
    loading, 
    error, 
    globalMetrics,
    exchangeMetrics
  };
};
